// Active geographic coverage. Postings + org addresses must resolve to a
// province in this list — anything else is blocked at the address picker
// with a "chưa hỗ trợ khu vực này" message. To open a new market, add an
// entry here; nothing else needs to change.

import {
  PROVINCE_GROUPS,
  normalizeVN,
  type DistrictOption,
  type ProvinceCode,
} from "./locations";

export type SupportedProvince = {
  code: ProvinceCode;
  name: string;
  // Strings (case + accent insensitive) we accept as belonging to this
  // province from geocode outputs (Nominatim `city/state`, Google place
  // chunks). Cover both the bare name and the "TP."/"Thành phố" prefixes
  // because providers are inconsistent.
  aliases: readonly string[];
};

export const SUPPORTED_PROVINCES: readonly SupportedProvince[] = [
  {
    code: "HCM",
    name: "TP. Hồ Chí Minh",
    aliases: [
      "TP. Hồ Chí Minh",
      "Thành phố Hồ Chí Minh",
      "Hồ Chí Minh",
      "TP HCM",
      "TPHCM",
      "Sài Gòn",
      "Saigon",
      "Ho Chi Minh",
      "Ho Chi Minh City",
      "HCMC",
    ],
  },
] as const;

export const SUPPORTED_PROVINCE_CODES = SUPPORTED_PROVINCES.map(
  (p) => p.code,
) as unknown as readonly [ProvinceCode, ...ProvinceCode[]];

// Human-readable list for the "chỉ hỗ trợ …" message. Currently just one
// entry — when we add HN this turns into "HCM & Hà Nội".
export const SUPPORTED_PROVINCES_LABEL_VI = SUPPORTED_PROVINCES.map(
  (p) => p.name,
).join(" & ");

// Pre-normalised alias index so resolution is a single map lookup.
const ALIAS_INDEX = (() => {
  const idx = new Map<string, SupportedProvince>();
  for (const p of SUPPORTED_PROVINCES) {
    for (const a of [p.name, ...p.aliases]) {
      idx.set(normalizeVN(a), p);
    }
  }
  return idx;
})();

// Resolve a province from any/all of the textual hints geocoders return
// (Nominatim's `city/state/county`, Google's `/place/` chunks). Returns
// null if none of the hints match a supported province — caller should
// surface an out-of-region message.
export function resolveSupportedProvince(
  hints: readonly (string | null | undefined)[],
): SupportedProvince | null {
  for (const h of hints) {
    if (!h) continue;
    const hit = ALIAS_INDEX.get(normalizeVN(h));
    if (hit) return hit;
  }
  return null;
}

// Districts for a given province. Used by AddressPicker to render the
// district `Select`. Returns [] if the province has no curated district
// list yet (we only ship the markets we serve — `SUPPORTED_PROVINCES`).
export function getDistrictsForProvince(
  code: ProvinceCode | null | undefined,
): readonly DistrictOption[] {
  if (!code) return [];
  const group = PROVINCE_GROUPS.find((g) => g.id.toUpperCase() === code);
  return group?.districts ?? [];
}

// Fuzzy-match free-text hints against the district catalog for a province.
// Strips "Quận"/"Huyện"/"TP." prefixes so Nominatim's "Phường Bàn Cờ" or
// Google's "Quận 3" both resolve to the same DistrictOption. Returns null
// when no hint matches — picker leaves the Select empty so the user can
// pick manually.
const DISTRICT_PREFIX = /^(quận|huyen|huyện|tp\.?|thanh pho|thành phố)\s+/i;
export function resolveDistrict(
  code: ProvinceCode | null | undefined,
  hints: readonly (string | null | undefined)[],
): DistrictOption | null {
  const districts = getDistrictsForProvince(code);
  if (!districts.length) return null;
  const index = new Map<string, DistrictOption>();
  for (const d of districts) {
    index.set(normalizeVN(d.label), d);
    const short = d.label.replace(DISTRICT_PREFIX, "").trim();
    if (short) index.set(normalizeVN(short), d);
  }
  for (const h of hints) {
    if (!h) continue;
    const key = normalizeVN(h);
    const hit = index.get(key);
    if (hit) return hit;
    // Strip prefix on the hint side too — Nominatim returns "Phường Bàn Cờ"
    // but the catalog might just have "Bàn Cờ" (rare but defensive).
    const stripped = normalizeVN(h.replace(DISTRICT_PREFIX, ""));
    if (stripped !== key) {
      const hit2 = index.get(stripped);
      if (hit2) return hit2;
    }
  }
  return null;
}
